import React, { useCallback, useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BalanceCard from "@/components/BalanceCard";
import QuickActions from "@/components/QuickActions";
import VirtualCard from "@/components/VirtualCard";
import TransactionList from "@/components/TransactionList";
import TransactionDetailModal from "@/components/TransactionDetailModal";
import RequestsInbox from "@/components/RequestsInbox";
import AddMoneyModal from "@/components/AddMoneyModal";
import SendMoneyModal from "@/components/SendMoneyModal";
import RequestMoneyModal from "@/components/RequestMoneyModal";
import QRModal from "@/components/QRModal";
import ProfileModal from "@/components/ProfileModal";
import BillsModal from "@/components/BillsModal";
import SplitModal from "@/components/SplitModal";
import BankTransferModal from "@/components/BankTransferModal";
import CardControlsModal from "@/components/CardControlsModal";
import InternationalTransferModal from "@/components/InternationalTransferModal";
import InsightsModal from "@/components/InsightsModal";
import FinancingModal from "@/components/FinancingModal";
import BottomNav from "@/components/BottomNav";
import ActivityView from "@/components/ActivityView";
import AdminStats from "@/components/AdminStats";
import AdminModal from "@/components/AdminModal";
import { toast } from "sonner";
import { Receipt, Smartphone, Users, Landmark, Globe, CreditCard, TrendingUp, Banknote, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";

export default function Dashboard() {
  const { t, lang, can } = useApp();
  const [tab, setTab] = useState("home");
  const [loadErr, setLoadErr] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });

  const [modal, setModal] = useState(null); // add | send | request | qr | profile
  const [qrTab, setQrTab] = useState("receive");
  const [selectedTx, setSelectedTx] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [w, tx, rq] = await Promise.all([
        api.get("/wallet"),
        api.get("/transactions", { params: { limit: 8 } }),
        api.get("/requests"),
      ]);
      setWallet(w.data);
      setTransactions(tx.data);
      setRequests(rq.data);
      setLoadErr(false);
      setRefreshKey((k) => k + 1);
    } catch {
      setLoadErr(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openQr = (which) => { setQrTab(which); setModal("qr"); };
  const soon = () => toast(t.comingSoon, { description: t.comingSoonDesc });
  const onTab = (k) => {
    if (k === "profile") { setModal("profile"); return; }
    if (k === "card") { setModal("card"); return; }
    setTab(k);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const services = [
    { key: "split", testid: "secondary-split", icon: Users, label: t.split, on: true, onClick: () => setModal("split") },
    { key: "insights", testid: "secondary-insights", icon: TrendingUp, label: t.insights, on: true, onClick: () => setModal("insights") },
    { key: "bank", testid: "secondary-bank", icon: Landmark, label: t.bankTransfer, on: can("bank_transfer"), onClick: () => setModal("bank") },
    { key: "international", testid: "secondary-international", icon: Globe, label: t.internationalTransfer, on: can("intl_transfer"), onClick: () => setModal("international") },
    { key: "bills", testid: "secondary-bills", icon: Receipt, label: t.bills, on: can("bills"), onClick: () => setModal("bills") },
    { key: "recharge", testid: "secondary-recharge", icon: Smartphone, label: t.recharge, on: can("bills"), onClick: () => setModal("bills") },
    { key: "financing", testid: "secondary-financing", icon: Banknote, label: t.financingShort, on: can("financing"), onClick: () => setModal("financing") },
    { key: "card", testid: "secondary-card", icon: CreditCard, label: t.cardControls, on: true, onClick: () => setModal("card") },
  ];

  return (
    <div className="max-w-md mx-auto min-h-screen relative pb-28 bg-background shadow-2xl sm:border-x border-border/60">
      <Header onProfile={() => setModal("profile")} />

      <div className="px-5">
        {loadErr && (
          <div data-testid="dashboard-error" className="mt-4 flex items-center gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            <span className="flex-1">{t.loadError}</span>
            <button onClick={refresh} className="font-bold text-primary">{t.retry}</button>
          </div>
        )}

        {tab === "home" && (<>
        <div className="mt-4">
          <BalanceCard wallet={wallet} onAddMoney={() => setModal("add")} />
        </div>

        <QuickActions
          onSend={() => setModal("send")}
          onRequest={() => setModal("request")}
          onAdd={() => setModal("add")}
          onScan={() => openQr("pay")}
        />

        <RequestsInbox requests={requests} onResolved={refresh} />

        <h3 className="text-sm font-bold text-muted-foreground mb-3">{t.servicesTitle}</h3>
        <div className="grid grid-cols-4 gap-2.5 mb-6" data-testid="services-grid">
          {services.map((sv) => (
            <button
              key={sv.key}
              data-testid={sv.testid}
              data-available={sv.on ? "true" : "false"}
              onClick={sv.on ? sv.onClick : soon}
              className={`relative flex flex-col items-center gap-1.5 pt-3 pb-2.5 px-1 rounded-2xl border transition-all active:scale-95 ${sv.on ? "bg-card border-border hover:border-primary/40 hover:bg-primary/5" : "bg-muted/40 border-transparent"}`}
            >
              {!sv.on && <span className="absolute top-1 end-1 text-[9px] font-bold px-1.5 py-px rounded-full bg-foreground/10 text-muted-foreground">{t.comingSoon}</span>}
              <span className={`w-10 h-10 rounded-xl grid place-items-center ${sv.on ? "bg-primary/10" : "bg-muted"}`}>
                <sv.icon className={`w-5 h-5 ${sv.on ? "text-primary" : "text-muted-foreground"}`} />
              </span>
              <span className={`text-[11px] font-semibold text-center leading-tight line-clamp-2 ${sv.on ? "" : "text-muted-foreground"}`}>{sv.label}</span>
            </button>
          ))}
        </div>

        {can("virtual_card") && <VirtualCard wallet={wallet} onManage={() => setModal("card")} />}

        <div className="mt-7">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-muted-foreground">{t.recentActivity}</h3>
            <button
              data-testid="see-all-activity"
              onClick={() => onTab("activity")}
              className="flex items-center gap-0.5 text-xs font-semibold text-primary hover:underline"
            >
              {t.navActivity} {lang === "ar" ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
          {!wallet && !loadErr
            ? <div className="space-y-2.5">{[0, 1, 2].map((i) => <div key={i} className="h-[68px] rounded-2xl bg-muted/60 animate-pulse" />)}</div>
            : <TransactionList transactions={transactions} onSelect={setSelectedTx} />}
        </div>
        </>)}

        {tab === "activity" && <ActivityView onSelect={setSelectedTx} refreshKey={refreshKey} />}
        {tab === "admin" && wallet?.is_admin && <AdminStats onCustomers={() => setModal("admin")} />}
      </div>

      <BottomNav tab={tab} onTab={onTab} isAdmin={!!wallet?.is_admin} />

      {/* Modals */}
      <AddMoneyModal open={modal === "add"} onClose={() => setModal(null)} onSuccess={refresh} />
      <SendMoneyModal open={modal === "send"} onClose={() => setModal(null)} onSuccess={refresh} />
      <RequestMoneyModal open={modal === "request"} onClose={() => setModal(null)} onSuccess={refresh} />
      <QRModal open={modal === "qr"} onClose={() => setModal(null)} onSuccess={refresh} wallet={wallet} defaultTab={qrTab} />
      <ProfileModal open={modal === "profile"} onClose={() => setModal(null)} onSuccess={refresh} />
      <BillsModal open={modal === "bills"} onClose={() => setModal(null)} onSuccess={refresh} />
      <SplitModal open={modal === "split"} onClose={() => setModal(null)} onSuccess={refresh} />
      <BankTransferModal open={modal === "bank"} onClose={() => setModal(null)} onSuccess={refresh} />
      <InternationalTransferModal open={modal === "international"} onClose={() => setModal(null)} onSuccess={refresh} />
      <CardControlsModal open={modal === "card"} onClose={() => setModal(null)} onSuccess={refresh} />
      <InsightsModal open={modal === "insights"} onClose={() => setModal(null)} />
      <FinancingModal open={modal === "financing"} onClose={() => setModal(null)} onSuccess={refresh} />
      <AdminModal open={modal === "admin"} onClose={() => setModal(null)} />
      <TransactionDetailModal tx={selectedTx} open={!!selectedTx} onClose={() => setSelectedTx(null)} />
    </div>
  );
}
